import { useMemo } from 'react';
import { Check, X } from 'lucide-react';

interface PasswordStrengthIndicatorProps {
  password: string;
  showRequirements?: boolean;
}

interface PasswordRequirement {
  id: string;
  label: string;
  regex: RegExp;
  met: boolean;
}

export function PasswordStrengthIndicator({ password, showRequirements = true }: PasswordStrengthIndicatorProps) {
  const requirements: PasswordRequirement[] = useMemo(() => [
    {
      id: 'length',
      label: 'At least 8 characters',
      regex: /.{8,}/,
      met: /.{8,}/.test(password),
    },
    {
      id: 'uppercase',
      label: 'At least one uppercase letter',
      regex: /[A-Z]/,
      met: /[A-Z]/.test(password),
    },
    {
      id: 'lowercase',
      label: 'At least one lowercase letter',
      regex: /[a-z]/,
      met: /[a-z]/.test(password),
    },
    {
      id: 'number',
      label: 'At least one number',
      regex: /[0-9]/,
      met: /[0-9]/.test(password),
    },
    {
      id: 'special',
      label: 'At least one special character',
      regex: /[@$!%*?&]/,
      met: /[@$!%*?&]/.test(password),
    },
  ], [password]);

  const strength = useMemo(() => {
    const score = requirements.filter(req => req.met).length;
    if (score === 0) return { level: 0, label: 'Very Weak', color: 'bg-red-500' };
    if (score <= 2) return { level: 1, label: 'Weak', color: 'bg-red-400' };
    if (score <= 3) return { level: 2, label: 'Fair', color: 'bg-white' };
    if (score <= 4) return { level: 3, label: 'Good', color: 'bg-blue-400' };
    return { level: 4, label: 'Strong', color: 'bg-green-500' };
  }, [requirements]);

  if (!password) return null;

  return (
    <div className="space-y-2">
      {/* Strength Meter */}
      <div className="space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Password Strength</span>
          <span className={`font-medium ${
            strength.level >= 3 ? 'text-green-600' : 
            strength.level >= 2 ? 'text-gray-700' : 
            'text-red-600'
          }`}>
            {strength.label}
          </span>
        </div>
        <div className="flex space-x-1">
          {[1, 2, 3, 4].map((level) => (
            <div
              key={level}
              className={`h-2 flex-1 rounded-full transition-colors ${
                level <= strength.level 
                  ? strength.color 
                  : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Requirements List */}
      {showRequirements && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Password must contain:</p>
          <div className="space-y-1">
            {requirements.map((requirement) => (
              <div
                key={requirement.id}
                className="flex items-center space-x-2 text-xs"
              >
                {requirement.met ? (
                  <Check className="h-3 w-3 text-green-600" />
                ) : (
                  <X className="h-3 w-3 text-gray-400" />
                )}
                <span
                  className={
                    requirement.met 
                      ? 'text-green-600' 
                      : 'text-muted-foreground'
                  }
                >
                  {requirement.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
